import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { LandingLayout } from "./screens/landing/layout";
import { SessionProvider } from "./services/auth0/session";
import { CarrierScopeProvider } from "./contexts/carrier-scope-context";
import LandingScreen from "./screens/landing/landing.screen";
import DispatchV2Screen from "./screens/dashboard/dispatch-v2/dispatch-v2.screen";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Auth0ProviderWithNavigate } from "./services/auth0/auth";
import { useSocketSetup } from "@/services/socket.io/useSocketSetup";
import OrdersScreen from "./screens/tables/orders.screen";
import OrdersTableScreen from "./screens/tables/orders.screen";
import CancellationsScreen from "./screens/tables/cancellations.screen";
import { ShipmentChangeRequestsScreen } from "./screens/tables/shipment-change-requests.screen";
import CustomersScreen from "./screens/tables/customers.screen";
import { TableLayout } from "./screens/tables/layout";
import AddressMediasScreen from "./screens/tables/address-medias.screen";
import { GoogleMapsApiProvider } from "./services/maps/google-maps-api-provider";
import AnnouncementsScreen from "./screens/tables/announcements.screen";
import NotificationLogsScreen from "./screens/tables/notification-logs.screen";
import { Toaster } from "@/components/ui/sonner";
import AnalyticsDashboard from "./features/analytics/analytics-dashboard";
import { CreateOrderPage } from "./features/orders/create-order-page";
import { BulkCreateScreen } from "./features/orders/bulk-create/bulk-create.screen";
import { ShipmentsPage } from "./screens/shipments/shipments-page";
import { TrackingPage } from "./screens/shipments/tracking-page";
import {
  PaymentsListScreen,
  ReceiptsScreen,
  InvoicesScreen,
  PaymentSettingsScreen as PaymentSettingsNewScreen,
  PaymentConfigScreen,
  PriceAdjustmentsScreen,
} from "./screens/payments";
import StuckOrdersScreen from "./screens/tables/stuck-orders.screen";
import UpcomingInvoicesScreen from "./screens/tables/upcoming-invoices.screen";
import { GeographicBoundaries } from "./screens/geographic-boundaries";
import PaymentSuccessScreen from "./screens/payment-success";
import { AddressBook } from "./features/address-book/pages/AddressBook";
import {
  TripsListScreen,
  TasksScreen as TripsTasksScreen,
} from "./screens/trips";
import {
  ServiceAreasListScreen,
  GeographicBoundariesScreen,
} from "./screens/service-areas";
import {
  PricingWorkspaceScreen,
  FxRatesScreen,
  PropertyTypeSurchargesScreen,
  TaxRatesScreen,
  DiscountsHubScreen,
  PickupBundleDiscountEditor,
} from "./screens/pricing";
import {
  PickupServicesScreen,
  PickupServiceDetailScreen,
  InsuranceServicesScreen,
  InsuranceServiceDetailScreen,
  PackagingServicesScreen,
  PackagingServiceDetailScreen,
  SignatureServicesScreen,
  SignatureServiceDetailScreen,
  SpecialHandlingServicesScreen,
  SpecialHandlingServiceDetailScreen,
  CarrierAddOnServiceGrantScreen,
} from "./screens/add-on-services";
import {
  PickupBookingsScreen,
  DispatchCentersScreen,
  PickupRefundRequestsScreen,
} from "./screens/pickup";
import {
  OperatorsScreen as TeamOperatorsScreen,
  UsersScreen,
} from "./screens/team";
import { SchedulingScreen } from "./screens/scheduling";
import {
  ApiProvidersScreen,
  CarriersListScreen,
  CarrierCustomerDiscountsScreen,
} from "./screens/carriers";
import { QueuesRedirectScreen, PlatformSettingsScreen } from "./screens/system";

const queryClient = new QueryClient();

const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  useSocketSetup({ closeOnUnmount: true });
  return <>{children}</>;
};

export const Router = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools />
        <Toaster />
        <Auth0ProviderWithNavigate>
          <SessionProvider>
            <SocketProvider>
              <GoogleMapsApiProvider>
                <CarrierScopeProvider>
                  <Routes>
                    <Route element={<LandingLayout />}>
                      <Route path="/" element={<LandingScreen />} />
                    </Route>
                    <Route path="/dispatch" element={<DispatchV2Screen />} />
                    {/* Retired dispatch bookmarks → canonical board. */}
                    <Route
                      path="/dispatch-legacy"
                      element={<Navigate to="/dispatch" replace />}
                    />
                    {/* Stale bookmarks → canonical /dispatch. */}
                    <Route
                      path="/dispatch-v2"
                      element={<Navigate to="/dispatch" replace />}
                    />
                    <Route
                      path="/dashboard"
                      element={<Navigate to="/dispatch" replace />}
                    />
                    <Route
                      path="/payment-success"
                      element={<PaymentSuccessScreen />}
                    />
                    <Route
                      path="/tracking/:trackingNumber"
                      element={<TrackingPage />}
                    />
                    <Route element={<TableLayout />}>
                      <Route
                        path="/fulfillment/orders"
                        element={<OrdersScreen />}
                      />
                      <Route
                        path="/fulfillment/orders/create"
                        element={<CreateOrderPage />}
                      />
                      <Route
                        path="/fulfillment/orders/bulk-create"
                        element={<BulkCreateScreen />}
                      />
                      <Route
                        path="/fulfillment/shipments"
                        element={<ShipmentsPage />}
                      />
                      <Route
                        path="/fulfillment/cancellations"
                        element={<CancellationsScreen />}
                      />
                      <Route
                        path="/fulfillment/change-requests"
                        element={<ShipmentChangeRequestsScreen />}
                      />
                      {/* Redirects from old routes */}
                      <Route
                        path="/orders"
                        element={<Navigate to="/fulfillment/orders" replace />}
                      />
                      <Route
                        path="/orders/create"
                        element={
                          <Navigate to="/fulfillment/orders/create" replace />
                        }
                      />
                      <Route
                        path="/shipments"
                        element={
                          <Navigate to="/fulfillment/shipments" replace />
                        }
                      />
                      <Route
                        path="/cancellations"
                        element={
                          <Navigate to="/fulfillment/cancellations" replace />
                        }
                      />
                      <Route
                        path="/shipment-change-requests"
                        element={
                          <Navigate to="/fulfillment/change-requests" replace />
                        }
                      />
                      <Route path="/team" element={<TeamOperatorsScreen />} />
                      <Route path="/team/users" element={<UsersScreen />} />
                      <Route
                        path="/scheduling"
                        element={<SchedulingScreen />}
                      />
                      <Route
                        path="/service-areas"
                        element={<ServiceAreasListScreen />}
                      />
                      <Route
                        path="/service-areas/boundaries"
                        element={<GeographicBoundariesScreen />}
                      />
                      <Route
                        path="/orders-table"
                        element={<OrdersTableScreen />}
                      />
                      <Route path="/customers" element={<CustomersScreen />} />
                      <Route
                        path="/payments"
                        element={<PaymentsListScreen />}
                      />
                      <Route
                        path="/payments/receipts"
                        element={<ReceiptsScreen />}
                      />
                      <Route
                        path="/payments/invoices"
                        element={<InvoicesScreen />}
                      />
                      <Route
                        path="/payments/stuck-orders"
                        element={<StuckOrdersScreen />}
                      />
                      <Route
                        path="/payments/upcoming-invoices"
                        element={<UpcomingInvoicesScreen />}
                      />
                      <Route
                        path="/payments/adjustments"
                        element={<PriceAdjustmentsScreen />}
                      />
                      <Route
                        path="/payments/settings"
                        element={<PaymentSettingsNewScreen />}
                      />
                      <Route
                        path="/payments/config"
                        element={<PaymentConfigScreen />}
                      />
                      <Route path="/trips" element={<TripsListScreen />} />
                      <Route
                        path="/trips/tasks"
                        element={<TripsTasksScreen />}
                      />
                      <Route
                        path="/address-medias"
                        element={<AddressMediasScreen />}
                      />
                      <Route
                        path="/carriers"
                        element={<ApiProvidersScreen />}
                      />
                      <Route
                        path="/carriers/list"
                        element={<CarriersListScreen />}
                      />
                      <Route
                        path="/carriers/depots"
                        element={
                          <Navigate to="/carriers/dispatch-centers" replace />
                        }
                      />
                      <Route
                        path="/carriers/customer-discounts"
                        element={<CarrierCustomerDiscountsScreen />}
                      />
                      {/* Add-On Services section: carrier add-on screens
                        regrouped under /add-on-services. Old /pricing and
                        /service-areas paths redirect here. */}
                      <Route
                        path="/add-on-services"
                        element={<PickupServicesScreen />}
                      />
                      <Route
                        path="/add-on-services/services/:id"
                        element={<PickupServiceDetailScreen />}
                      />
                      <Route
                        path="/add-on-services/insurance"
                        element={<InsuranceServicesScreen />}
                      />
                      <Route
                        path="/add-on-services/insurance/:id"
                        element={<InsuranceServiceDetailScreen />}
                      />
                      <Route
                        path="/add-on-services/packaging"
                        element={<PackagingServicesScreen />}
                      />
                      <Route
                        path="/add-on-services/packaging/:id"
                        element={<PackagingServiceDetailScreen />}
                      />
                      <Route
                        path="/add-on-services/signature"
                        element={<SignatureServicesScreen />}
                      />
                      <Route
                        path="/add-on-services/signature/:id"
                        element={<SignatureServiceDetailScreen />}
                      />
                      <Route
                        path="/add-on-services/special-handling"
                        element={<SpecialHandlingServicesScreen />}
                      />
                      <Route
                        path="/add-on-services/special-handling/:id"
                        element={<SpecialHandlingServiceDetailScreen />}
                      />
                      <Route
                        path="/add-on-services/grants"
                        element={<CarrierAddOnServiceGrantScreen />}
                      />
                      <Route
                        path="/add-on-services/enablement"
                        element={<Navigate to="/add-on-services" replace />}
                      />
                      <Route
                        path="/add-on-services/pickup-rates"
                        element={<Navigate to="/add-on-services" replace />}
                      />
                      {/* Pricing-merger Phase 8: unified workspace.
                        Three rate types share one screen scoped by carrier
                        service + date. Old per-leg routes redirect here. */}
                      <Route
                        path="/pricing"
                        element={<PricingWorkspaceScreen />}
                      />
                      <Route
                        path="/pricing/carrier-rate-bands"
                        element={<Navigate to="/pricing" replace />}
                      />
                      <Route
                        path="/pricing/pickup-rate-bands"
                        element={<Navigate to="/pricing" replace />}
                      />
                      <Route
                        path="/pricing/redistribution-rate-bands"
                        element={<Navigate to="/pricing" replace />}
                      />
                      <Route
                        path="/pricing/fx-rates"
                        element={<FxRatesScreen />}
                      />
                      <Route
                        path="/pickup-bookings"
                        element={<PickupBookingsScreen />}
                      />
                      <Route
                        path="/refund-requests"
                        element={<PickupRefundRequestsScreen />}
                      />
                      <Route
                        path="/pickup-refund-requests"
                        element={<Navigate to="/refund-requests" replace />}
                      />
                      <Route
                        path="/carriers/dispatch-centers"
                        element={<DispatchCentersScreen />}
                      />
                      {/* Pricing-merger Phase 9: /pricing/markups removed —
                        EZX fee edited inline from the unified workspace.
                        Bookmarked URL 404s cleanly (no redirect). */}
                      {/* Pricing-merger Phase 6: /pricing/dimensional and
                        /pricing/acceptance routes removed (screens + APIs
                        deleted, server endpoints gone). */}
                      <Route
                        path="/pricing/surcharges"
                        element={
                          <Navigate to="/add-on-services/insurance" replace />
                        }
                      />
                      <Route
                        path="/pricing/property-type-surcharges"
                        element={<PropertyTypeSurchargesScreen />}
                      />
                      <Route
                        path="/pricing/tax-rates"
                        element={<TaxRatesScreen />}
                      />
                      <Route
                        path="/pricing/pickup-services"
                        element={<Navigate to="/add-on-services" replace />}
                      />
                      <Route
                        path="/pricing/carrier-add-on-service-grants"
                        element={
                          <Navigate to="/add-on-services/grants" replace />
                        }
                      />
                      <Route
                        path="/pricing/carrier-add-on-service-access"
                        element={
                          <Navigate to="/add-on-services/enablement" replace />
                        }
                      />
                      <Route
                        path="/pricing/discounts"
                        element={<DiscountsHubScreen />}
                      />
                      <Route
                        path="/pricing/discounts/bundle"
                        element={<PickupBundleDiscountEditor />}
                      />
                      <Route
                        path="/analytics"
                        element={<AnalyticsDashboard />}
                      />
                      <Route
                        path="/announcements"
                        element={<AnnouncementsScreen />}
                      />
                      <Route
                        path="/notification-logs"
                        element={<NotificationLogsScreen />}
                      />
                      <Route
                        path="/geographic-boundaries"
                        element={<GeographicBoundaries />}
                      />
                      <Route path="/contacts" element={<AddressBook />} />
                      <Route
                        path="/system/queues"
                        element={<QueuesRedirectScreen />}
                      />
                      <Route
                        path="/system/platform-settings"
                        element={<PlatformSettingsScreen />}
                      />
                    </Route>
                  </Routes>
                </CarrierScopeProvider>
              </GoogleMapsApiProvider>
            </SocketProvider>
          </SessionProvider>
        </Auth0ProviderWithNavigate>
      </QueryClientProvider>
    </BrowserRouter>
  );
};
